import { html } from 'lit';
import type { Shop, ShopAttribute } from '../chat/types.js';
import { config } from '../../config/environment.js';

const ORIGINAL_COLOR_AMENITIES = new Set(['Continente Siga', 'Glovo', 'PIX', 'Plug&Charge']);

export function isOriginalColorAmenity(name: string | undefined): boolean {
  return !!name && ORIGINAL_COLOR_AMENITIES.has(name);
}

export function getBrandsWithLogo(brands: ShopAttribute[] | undefined): ShopAttribute[] {
  return brands?.filter(brand => !!brand.image) ?? [];
}

export function getShopImage(shop: Shop): string | undefined {
  return shop.images?.[0] || config.ASSETS.DEFAULT_STORE_IMAGE || undefined;
}

export function getPostalAdminLine(shop: Shop): string {
  const cityPart = [shop.postal_code, shop.admin_div_2_name].filter(Boolean).join(' ').trim();
  return [cityPart, shop.admin_div_1_name].filter(Boolean).join(', ');
}

function splitScheduleLines(source: string): string[] {
  return source.split(/[\n;]/).map(line => line.trim()).filter(Boolean);
}

export function isSpecialScheduleForToday(shop: Shop): boolean {
  const special = shop.special_schedule?.trim();
  if (!special) return false;

  const match = special.match(/^(\d{1,2})\/(\d{1,2})/);
  if (!match) return false;

  const day = Number(match[1]);
  const month = Number(match[2]);
  if (!day || !month) return false;

  const today = new Date();
  return day === today.getDate() && month === today.getMonth() + 1;
}

export function getHoursLines(shop: Shop): string[] {
  if (isSpecialScheduleForToday(shop)) {
    return [shop.special_schedule!.trim()];
  }

  return splitScheduleLines(shop.today_schedule || '');
}

export function getFullScheduleLines(shop: Shop): string[] {
  return splitScheduleLines(shop.schedule || '');
}

export function getShopStatus(shop: Shop): 'open' | 'closed' | undefined {
  if (typeof shop.open_now !== 'boolean') return undefined;
  return shop.open_now ? 'open' : 'closed';
}

export function getDistanceLabel(shop: Shop): string | undefined {
  if (shop.distance_km === null || shop.distance_km === undefined) return undefined;
  return `${shop.distance_km} km`;
}

export function renderShopStatusTag(shop: Shop, isSpecial: boolean = !!shop.special_schedule?.trim()) {
  const status = getShopStatus(shop);
  if (!status) return '';

  const label = status === 'open' ? 'Aberto' : 'Fechado';
  const variant = isSpecial ? 'special' : status;

  return html`<span class="shop-status-tag shop-status-tag--${variant}">${label}</span>`;
}
